/**
 * Sub-spec 05 Plan 5 M1 — per-groomer detail page.
 *
 * Combines three settings into one route for v1:
 *   - Capable services (multi-select against `services` where category='grooming').
 *   - Per-groomer pricing overrides (`pricing_rules` rows with kind='groomer_specific').
 *   - Default weekly schedule + schedule exceptions.
 *
 * Plan 1 shipped the roster row + create modal; Plan 5 fills in the editing
 * surface. Per the plan doc, schedule exceptions get a simple inline editor —
 * the full ScheduleExceptionEditor modal pattern lands in a future v1.x polish.
 *
 * Permissions: `bookings.create` for read, `grooming.configure` for write.
 * The page swallows 403 from any write call into an inline error banner; the
 * roster page already 403-gates the entry point so we won't reach here without
 * read permission.
 */
import { useAuthContext } from '@/auth/useAuthContext';
import {
  ApiError,
  type GroomerDefaultSchedule,
  type GroomerScheduleException,
  type ScheduleDay,
  createGroomerScheduleException,
  createPricingRule,
  deleteGroomerScheduleException,
  deletePricingRule,
  getGroomer,
  getGroomerSchedule,
  listPricingRules,
  listServices,
  setGroomerSchedule,
  updateGroomer,
  updatePricingRule,
} from '@/lib/api';
import type { Groomer, PricingRule, ServiceCatalogueItem } from '@/lib/types';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

const DAY_LABELS: Array<{ key: '1' | '2' | '3' | '4' | '5' | '6' | '7'; label: string }> = [
  { key: '1', label: 'Mon' },
  { key: '2', label: 'Tue' },
  { key: '3', label: 'Wed' },
  { key: '4', label: 'Thu' },
  { key: '5', label: 'Fri' },
  { key: '6', label: 'Sat' },
  { key: '7', label: 'Sun' },
];

const HH_MM_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

function parseScopeJson(s: string): { groomer_id?: string; service_id?: string } {
  try {
    const v = JSON.parse(s) as unknown;
    if (v && typeof v === 'object' && !Array.isArray(v)) {
      const o = v as Record<string, unknown>;
      const out: { groomer_id?: string; service_id?: string } = {};
      if (typeof o.groomer_id === 'string') out.groomer_id = o.groomer_id;
      if (typeof o.service_id === 'string') out.service_id = o.service_id;
      return out;
    }
  } catch {
    // fall through
  }
  return {};
}

function pricesEqual(base: number, override: number): boolean {
  return Math.round(base) === Math.round(override);
}

interface PricingRowDraft {
  serviceId: string;
  serviceName: string;
  basePriceMinor: number;
  /** The tenant's intended target price in minor units, OR null if untouched. */
  overrideMinor: number | null;
  /** Existing rule for this service (if any) so we can update/delete. */
  existingRule: PricingRule | null;
}

function buildPricingDrafts(
  groomer: Groomer,
  services: ServiceCatalogueItem[],
  rules: PricingRule[],
): PricingRowDraft[] {
  // Capable services: NULL = wildcard (all grooming services), else explicit array.
  let capable: string[] | null;
  if (groomer.capableServiceIds === null) {
    capable = null;
  } else {
    try {
      capable = JSON.parse(groomer.capableServiceIds) as string[];
    } catch {
      capable = [];
    }
  }

  const groomingServices = services.filter(
    (s) =>
      s.category === 'grooming' && s.isActive === 1 && (capable === null || capable.includes(s.id)),
  );

  const groomerRules = rules.filter((r) => {
    if (r.kind !== 'groomer_specific') return false;
    const scope = parseScopeJson(r.scopeJson);
    return scope.groomer_id === groomer.id;
  });

  return groomingServices.map((s) => {
    const existing =
      groomerRules.find((r) => parseScopeJson(r.scopeJson).service_id === s.id) ?? null;
    let overrideMinor: number | null = null;
    if (existing && existing.modifierKind === 'fixed_minor' && existing.modifierBasis === 'base') {
      overrideMinor = s.defaultPriceMinor + existing.modifierValue;
    }
    return {
      serviceId: s.id,
      serviceName: s.name,
      basePriceMinor: s.defaultPriceMinor,
      overrideMinor,
      existingRule: existing,
    };
  });
}

interface ScheduleDayDraft {
  enabled: boolean;
  startTime: string;
  endTime: string;
}

function scheduleToDrafts(schedule: GroomerDefaultSchedule): Record<string, ScheduleDayDraft> {
  const out: Record<string, ScheduleDayDraft> = {};
  for (const { key } of DAY_LABELS) {
    const day = schedule?.[key];
    if (day) {
      out[key] = { enabled: true, startTime: day.startTime, endTime: day.endTime };
    } else {
      out[key] = { enabled: false, startTime: '09:00', endTime: '17:00' };
    }
  }
  return out;
}

function draftsToSchedule(drafts: Record<string, ScheduleDayDraft>): GroomerDefaultSchedule {
  const out: Record<string, ScheduleDay | null> = {};
  for (const { key } of DAY_LABELS) {
    const d = drafts[key];
    if (!d || !d.enabled) {
      out[key] = null;
      continue;
    }
    out[key] = { startTime: d.startTime, endTime: d.endTime, breaks: [] };
  }
  return out;
}

function formatMinor(minor: number): string {
  return (minor / 100).toFixed(2);
}

function parseMajor(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

export function GroomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const facilities = state.me?.facilitiesInScope ?? [];

  const [groomer, setGroomer] = useState<Groomer | null>(null);
  const [services, setServices] = useState<ServiceCatalogueItem[]>([]);
  const [rules, setRules] = useState<PricingRule[]>([]);
  const [, setSchedule] = useState<GroomerDefaultSchedule>(null);
  const [exceptions, setExceptions] = useState<GroomerScheduleException[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [permissionDenied, setPermissionDenied] = useState(false);

  // ---- Capable services ----
  const [capableDraft, setCapableDraft] = useState<{ wildcard: boolean; ids: string[] }>({
    wildcard: true,
    ids: [],
  });
  const [savingCapable, setSavingCapable] = useState(false);
  const [capableError, setCapableError] = useState<string | null>(null);
  const [capableSaved, setCapableSaved] = useState(false);

  // ---- Pricing ----
  const [pricingDrafts, setPricingDrafts] = useState<PricingRowDraft[]>([]);
  const [pricingInputs, setPricingInputs] = useState<Record<string, string>>({});
  const [savingPricing, setSavingPricing] = useState(false);
  const [pricingError, setPricingError] = useState<string | null>(null);
  const [pricingSaved, setPricingSaved] = useState(false);

  // ---- Schedule ----
  const [scheduleDrafts, setScheduleDrafts] = useState<Record<string, ScheduleDayDraft>>(() =>
    scheduleToDrafts(null),
  );
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [scheduleSaved, setScheduleSaved] = useState(false);

  // ---- Exceptions ----
  const [newExceptionDate, setNewExceptionDate] = useState('');
  const [newExceptionType, setNewExceptionType] = useState<'unavailable' | 'shifted'>(
    'unavailable',
  );
  const [newExceptionStart, setNewExceptionStart] = useState('09:00');
  const [newExceptionEnd, setNewExceptionEnd] = useState('17:00');
  const [newExceptionNote, setNewExceptionNote] = useState('');
  const [addingException, setAddingException] = useState(false);
  const [exceptionError, setExceptionError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setLoadError(null);
    setPermissionDenied(false);
    try {
      const [{ groomer: g }, svc, rls, sched] = await Promise.all([
        getGroomer(id),
        listServices().catch(() => [] as ServiceCatalogueItem[]),
        listPricingRules()
          .then((r) => r.rules)
          .catch(() => [] as PricingRule[]),
        getGroomerSchedule(id),
      ]);
      setGroomer(g);
      setServices(svc);
      setRules(rls);
      setSchedule(sched.defaultSchedule);
      setExceptions(sched.exceptions);

      // Initialise capable-services draft.
      if (g.capableServiceIds === null) {
        setCapableDraft({ wildcard: true, ids: [] });
      } else {
        let ids: string[] = [];
        try {
          ids = JSON.parse(g.capableServiceIds) as string[];
        } catch {
          ids = [];
        }
        setCapableDraft({ wildcard: false, ids });
      }

      // Schedule drafts.
      setScheduleDrafts(scheduleToDrafts(sched.defaultSchedule));
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setPermissionDenied(true);
      else if (err instanceof ApiError && err.status === 404) setLoadError('Groomer not found.');
      else setLoadError('Failed to load groomer.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Rebuild pricing drafts whenever the underlying data changes.
  useEffect(() => {
    if (!groomer) return;
    const drafts = buildPricingDrafts(groomer, services, rules);
    setPricingDrafts(drafts);
    const inputs: Record<string, string> = {};
    for (const d of drafts) {
      inputs[d.serviceId] =
        d.overrideMinor === null ? formatMinor(d.basePriceMinor) : formatMinor(d.overrideMinor);
    }
    setPricingInputs(inputs);
  }, [groomer, services, rules]);

  const groomingServices = useMemo(
    () => services.filter((s) => s.category === 'grooming' && s.isActive === 1),
    [services],
  );

  const facilityLabel = useMemo(() => {
    if (!groomer) return '';
    try {
      const ids = JSON.parse(groomer.workingFacilityIds) as string[];
      return ids
        .map((fid) => facilities.find((f) => f.id === fid)?.name ?? fid)
        .filter((v) => v.length > 0)
        .join(', ');
    } catch {
      return '';
    }
  }, [groomer, facilities]);

  // ---------------------------------------------------------------------------
  // Save handlers
  // ---------------------------------------------------------------------------

  async function handleSaveCapable() {
    if (!id) return;
    setSavingCapable(true);
    setCapableError(null);
    setCapableSaved(false);
    try {
      await updateGroomer(id, {
        capableServiceIds: capableDraft.wildcard ? null : capableDraft.ids,
      });
      setCapableSaved(true);
      await refresh();
    } catch (err) {
      setCapableError(
        err instanceof ApiError ? `Save failed (HTTP ${err.status}).` : 'Save failed.',
      );
    } finally {
      setSavingCapable(false);
    }
  }

  async function handleSavePricing() {
    if (!id || !groomer) return;
    setSavingPricing(true);
    setPricingError(null);
    setPricingSaved(false);
    try {
      // For each row: compute desired override price, compare against base,
      // and create/update/delete the rule.
      for (const d of pricingDrafts) {
        const inputVal = pricingInputs[d.serviceId] ?? formatMinor(d.basePriceMinor);
        const targetMinor = parseMajor(inputVal);
        if (targetMinor === null) {
          throw new Error(`Invalid price for ${d.serviceName}`);
        }
        const delta = targetMinor - d.basePriceMinor;
        const targetEqualsBase = pricesEqual(d.basePriceMinor, targetMinor);

        if (d.existingRule) {
          if (targetEqualsBase) {
            // Delete (soft) the existing rule — no override required anymore.
            await deletePricingRule(d.existingRule.id);
          } else {
            // Update the existing rule's modifier value.
            await updatePricingRule(d.existingRule.id, { modifierValue: delta });
          }
        } else if (!targetEqualsBase) {
          // Create a fresh rule.
          await createPricingRule({
            kind: 'groomer_specific',
            scopeJson: JSON.stringify({ groomer_id: groomer.id, service_id: d.serviceId }),
            modifierKind: 'fixed_minor',
            modifierBasis: 'base',
            modifierValue: delta,
            label: `${groomer.displayName} — ${d.serviceName}`,
          });
        }
      }
      setPricingSaved(true);
      await refresh();
    } catch (err) {
      setPricingError(
        err instanceof Error ? err.message : 'Pricing save failed. Please try again.',
      );
    } finally {
      setSavingPricing(false);
    }
  }

  async function handleSaveSchedule() {
    if (!id) return;
    setSavingSchedule(true);
    setScheduleError(null);
    setScheduleSaved(false);
    try {
      // Validate HH:MM + start < end on each enabled day.
      for (const { key, label } of DAY_LABELS) {
        const d = scheduleDrafts[key];
        if (!d || !d.enabled) continue;
        if (!HH_MM_PATTERN.test(d.startTime) || !HH_MM_PATTERN.test(d.endTime)) {
          throw new Error(`${label}: times must be HH:MM`);
        }
        if (!(d.startTime < d.endTime)) {
          throw new Error(`${label}: start time must be before end time`);
        }
      }
      const out = draftsToSchedule(scheduleDrafts);
      await setGroomerSchedule(id, out);
      setScheduleSaved(true);
      await refresh();
    } catch (err) {
      setScheduleError(
        err instanceof Error ? err.message : 'Schedule save failed. Please try again.',
      );
    } finally {
      setSavingSchedule(false);
    }
  }

  async function handleAddException() {
    if (!id) return;
    setAddingException(true);
    setExceptionError(null);
    try {
      const body: Parameters<typeof createGroomerScheduleException>[1] = {
        date: newExceptionDate,
        overrideType: newExceptionType,
      };
      if (newExceptionType === 'shifted') {
        body.startAt = newExceptionStart;
        body.endAt = newExceptionEnd;
      }
      if (newExceptionNote.trim().length > 0) {
        body.note = newExceptionNote.trim();
      }
      await createGroomerScheduleException(id, body);
      setNewExceptionDate('');
      setNewExceptionNote('');
      await refresh();
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setExceptionError('An exception already exists for this date.');
      } else if (err instanceof ApiError && err.status === 400) {
        setExceptionError('Please check the date + times.');
      } else {
        setExceptionError('Failed to add exception.');
      }
    } finally {
      setAddingException(false);
    }
  }

  async function handleDeleteException(exid: string) {
    if (!id) return;
    try {
      await deleteGroomerScheduleException(id, exid);
      await refresh();
    } catch {
      // Surface silently — refresh will re-pull if it succeeded but threw on parse.
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (permissionDenied) {
    return (
      <div className="rounded-2xl bg-surface p-12 text-center ring-1 ring-border elevate">
        <p className="text-sm font-medium text-foreground">
          You don't have permission to view this groomer.
        </p>
      </div>
    );
  }

  if (loading || !groomer) {
    return <p className="text-sm text-muted-foreground">{loadError ?? 'Loading…'}</p>;
  }

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => navigate('/settings/grooming/groomers')}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-3.5" strokeWidth={1.75} /> Back to groomers
      </button>

      <header>
        <h1 className="font-heading text-2xl font-medium tracking-tight">{groomer.displayName}</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          {facilityLabel ? `Working at ${facilityLabel}.` : 'No facilities configured.'}
          {groomer.isActive === 1 ? '' : ' Inactive.'}
        </p>
      </header>

      {/* Capable services */}
      <section
        data-testid="capable-services-section"
        className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border"
      >
        <h2 className="mb-1 font-heading text-lg font-medium">Services this groomer can perform</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Wildcard means the groomer can be booked for any active grooming service.
        </p>

        <label className="mb-3 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={capableDraft.wildcard}
            onChange={(e) => setCapableDraft((prev) => ({ ...prev, wildcard: e.target.checked }))}
          />
          All services (wildcard)
        </label>

        {!capableDraft.wildcard && (
          <div className="space-y-1">
            {groomingServices.length === 0 ? (
              <p className="text-xs text-muted-foreground">
                No grooming services configured. Add some on the Services page first.
              </p>
            ) : (
              groomingServices.map((s) => (
                <label key={s.id} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={capableDraft.ids.includes(s.id)}
                    onChange={(e) => {
                      setCapableDraft((prev) => ({
                        ...prev,
                        ids: e.target.checked
                          ? [...prev.ids, s.id]
                          : prev.ids.filter((x) => x !== s.id),
                      }));
                    }}
                  />
                  {s.name}
                </label>
              ))
            )}
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSaveCapable()}
            disabled={savingCapable}
            data-testid="capable-services-save"
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {savingCapable ? 'Saving…' : 'Save services'}
          </button>
          {capableError && <span className="text-sm text-danger">{capableError}</span>}
          {capableSaved && <span className="text-sm text-muted-foreground">Saved.</span>}
        </div>
      </section>

      {/* Per-groomer pricing overrides */}
      <section
        data-testid="pricing-section"
        className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border"
      >
        <h2 className="mb-1 font-heading text-lg font-medium">Per-groomer pricing overrides</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Enter the price you want this groomer to charge for each service. Setting the price equal
          to the base removes the override.
        </p>

        {pricingDrafts.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            This groomer has no capable services yet. Save services above first.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-[440px] w-full text-left text-sm">
              <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="py-2 font-medium">Service</th>
                  <th className="py-2 font-medium">Base price</th>
                  <th className="py-2 font-medium">Groomer's price</th>
                </tr>
              </thead>
              <tbody>
                {pricingDrafts.map((d) => (
                  <tr key={d.serviceId} className="border-b border-border last:border-b-0">
                    <td className="py-2 font-medium text-foreground">{d.serviceName}</td>
                    <td className="py-2 text-muted-foreground">
                      <span className="num">{formatMinor(d.basePriceMinor)}</span>
                    </td>
                    <td className="py-2">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={pricingInputs[d.serviceId] ?? ''}
                        onChange={(e) =>
                          setPricingInputs((prev) => ({ ...prev, [d.serviceId]: e.target.value }))
                        }
                        data-testid={`pricing-input-${d.serviceId}`}
                        className="num w-24 rounded-lg border border-border bg-background px-2 py-1 text-right"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSavePricing()}
            disabled={savingPricing || pricingDrafts.length === 0}
            data-testid="pricing-save"
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {savingPricing ? 'Saving…' : 'Save pricing'}
          </button>
          {pricingError && <span className="text-sm text-danger">{pricingError}</span>}
          {pricingSaved && <span className="text-sm text-muted-foreground">Saved.</span>}
        </div>
      </section>

      {/* Default weekly schedule */}
      <section
        data-testid="schedule-section"
        className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border"
      >
        <h2 className="mb-1 font-heading text-lg font-medium">Default weekly schedule</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          Tick a day to mark it as a working day, then set start and end times (24h format).
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-[420px] w-full text-left text-sm">
            <thead className="border-b border-border text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="py-2 font-medium">Day</th>
                <th className="py-2 font-medium">Working</th>
                <th className="py-2 font-medium">Start</th>
                <th className="py-2 font-medium">End</th>
              </tr>
            </thead>
            <tbody>
              {DAY_LABELS.map(({ key, label }) => {
                const d = scheduleDrafts[key] ?? {
                  enabled: false,
                  startTime: '09:00',
                  endTime: '17:00',
                };
                return (
                  <tr key={key} className="border-b border-border last:border-b-0">
                    <td className="py-2 font-medium text-foreground">{label}</td>
                    <td className="py-2">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) =>
                          setScheduleDrafts((prev) => ({
                            ...prev,
                            [key]: { ...d, enabled: e.target.checked },
                          }))
                        }
                        data-testid={`schedule-enabled-${key}`}
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="text"
                        value={d.startTime}
                        disabled={!d.enabled}
                        onChange={(e) =>
                          setScheduleDrafts((prev) => ({
                            ...prev,
                            [key]: { ...d, startTime: e.target.value },
                          }))
                        }
                        className="num w-20 rounded-lg border border-border bg-background px-2 py-1 disabled:opacity-50"
                        data-testid={`schedule-start-${key}`}
                      />
                    </td>
                    <td className="py-2">
                      <input
                        type="text"
                        value={d.endTime}
                        disabled={!d.enabled}
                        onChange={(e) =>
                          setScheduleDrafts((prev) => ({
                            ...prev,
                            [key]: { ...d, endTime: e.target.value },
                          }))
                        }
                        className="num w-20 rounded-lg border border-border bg-background px-2 py-1 disabled:opacity-50"
                        data-testid={`schedule-end-${key}`}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleSaveSchedule()}
            disabled={savingSchedule}
            data-testid="schedule-save"
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {savingSchedule ? 'Saving…' : 'Save schedule'}
          </button>
          {scheduleError && <span className="text-sm text-danger">{scheduleError}</span>}
          {scheduleSaved && <span className="text-sm text-muted-foreground">Saved.</span>}
        </div>
      </section>

      {/* Schedule exceptions */}
      <section
        data-testid="exceptions-section"
        className="mb-8 rounded-2xl bg-surface p-6 ring-1 ring-border"
      >
        <h2 className="mb-1 font-heading text-lg font-medium">Schedule exceptions</h2>
        <p className="mb-4 text-xs text-muted-foreground">
          One-off overrides for specific dates — for example a day off or a half-day.
        </p>

        {exceptions.length === 0 ? (
          <p className="text-xs text-muted-foreground">No upcoming exceptions.</p>
        ) : (
          <ul className="mb-4 space-y-2">
            {exceptions.map((ex) => (
              <li
                key={ex.id}
                className="flex items-center justify-between gap-3 rounded-lg bg-surface-2 px-3 py-2 text-sm"
                data-testid={`exception-row-${ex.id}`}
              >
                <div>
                  <span className="num font-medium text-foreground">{ex.date}</span>{' '}
                  <span className="text-muted-foreground">
                    {ex.overrideType === 'unavailable'
                      ? '· Unavailable'
                      : `· Shifted ${ex.startAt ?? ''}–${ex.endAt ?? ''}`}
                  </span>
                  {ex.note && (
                    <span className="ml-2 text-xs text-muted-foreground">— {ex.note}</span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => void handleDeleteException(ex.id)}
                  className="rounded-full px-2 py-0.5 text-xs text-muted-foreground hover:bg-secondary hover:text-foreground"
                  data-testid={`exception-delete-${ex.id}`}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        )}

        <div className="grid gap-3 md:grid-cols-5 md:items-end">
          <div>
            <label
              htmlFor="ex-date"
              className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
            >
              Date
            </label>
            <input
              id="ex-date"
              type="date"
              value={newExceptionDate}
              onChange={(e) => setNewExceptionDate(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              data-testid="new-exception-date"
            />
          </div>
          <div>
            <label
              htmlFor="ex-type"
              className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
            >
              Type
            </label>
            <select
              id="ex-type"
              value={newExceptionType}
              onChange={(e) =>
                setNewExceptionType(e.target.value === 'shifted' ? 'shifted' : 'unavailable')
              }
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
              data-testid="new-exception-type"
            >
              <option value="unavailable">Unavailable</option>
              <option value="shifted">Shifted hours</option>
            </select>
          </div>
          {newExceptionType === 'shifted' && (
            <>
              <div>
                <label
                  htmlFor="ex-start"
                  className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  Start (HH:MM)
                </label>
                <input
                  id="ex-start"
                  type="text"
                  value={newExceptionStart}
                  onChange={(e) => setNewExceptionStart(e.target.value)}
                  className="num w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label
                  htmlFor="ex-end"
                  className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
                >
                  End (HH:MM)
                </label>
                <input
                  id="ex-end"
                  type="text"
                  value={newExceptionEnd}
                  onChange={(e) => setNewExceptionEnd(e.target.value)}
                  className="num w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
                />
              </div>
            </>
          )}
          <div className="md:col-span-5">
            <label
              htmlFor="ex-note"
              className="mb-1 block text-[11px] font-medium uppercase tracking-wider text-muted-foreground"
            >
              Note (optional)
            </label>
            <input
              id="ex-note"
              type="text"
              value={newExceptionNote}
              onChange={(e) => setNewExceptionNote(e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => void handleAddException()}
            disabled={addingException || newExceptionDate.length === 0}
            data-testid="exception-add"
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {addingException ? 'Adding…' : 'Add exception'}
          </button>
          {exceptionError && <span className="text-sm text-danger">{exceptionError}</span>}
        </div>
      </section>

      <p className="text-xs text-muted-foreground">
        <Link to="/settings/grooming/groomers" className="underline">
          Back to roster
        </Link>
      </p>
    </div>
  );
}
